'use client';

import { CheckIcon, CopyIcon, LogOutIcon, UserIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';

import { globalStore } from '@/app/globalStore';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth as useFirebaseAuth } from '@/context/AuthContext';

interface UserAvatarProps {
  /** If provided, shows this user's avatar instead of current user */
  userId?: string;
  /** Size of the avatar */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the dropdown menu (only works for current user) */
  showDropdown?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether the avatar is disabled */
  disabled?: boolean;
  /**
   * When `userId` is another user, optional display name for initials fallback.
   * Ignored for the current signed-in user (their session profile is used instead).
   */
  displayNameOverride?: string | null;
  /**
   * When `userId` is another user, optional image URL (e.g. from Clerk public profile).
   * Ignored for the current signed-in user (their session profile is used instead).
   */
  imageUrlOverride?: string | null;
}

function initialsFromDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts[parts.length - 1]?.charAt(0) ?? '';
    return `${first}${last}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

const sizeClasses = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10'
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5'
};

export const UserAvatar = observer(({
  userId,
  size = 'md',
  showDropdown = true,
  className = '',
  disabled = false,
  displayNameOverride = null,
  imageUrlOverride = null,
}: UserAvatarProps) => {
  const { logout } = useFirebaseAuth();
  const [copied, setCopied] = useState(false);

  // Determine if this is the current user or another user
  const isCurrentUser = !userId || globalStore.currentUser.isOwner(userId);

  // Get user info - either current user or placeholder for other users
  const userInfo = isCurrentUser ? globalStore.currentUser.userInfo : null;

  const resolvedImageUrl = isCurrentUser
    ? userInfo?.imageUrl
    : (imageUrlOverride ?? null);

  const resolvedDisplayName = isCurrentUser
    ? (userInfo?.fullName ?? userInfo?.userName ?? null)
    : (displayNameOverride ?? null);

  // Generate user initials
  const userInitials = useMemo(() => {
    if (isCurrentUser && userInfo) {
      return globalStore.currentUser.userInitials;
    }
    if (!isCurrentUser && resolvedDisplayName) {
      return initialsFromDisplayName(resolvedDisplayName);
    }
    return '';
  }, [isCurrentUser, userInfo, resolvedDisplayName]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(globalStore.currentUser.uid ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  // Avatar content
  const avatarContent = (
    <Avatar className={`${sizeClasses[size]} outline outline-border ${className}`}>
      {resolvedImageUrl ? (
        <AvatarImage
          src={resolvedImageUrl}
          alt={resolvedDisplayName ?? userInfo?.fullName ?? userInfo?.userName ?? 'User'}
        />
      ) : null}
      <AvatarFallback className={`bg-[#5685EF] text-primary-foreground outline outline-border ${className}`}>
        {userInitials ? userInitials : <UserIcon className={iconSizes[size]} />}
      </AvatarFallback>
    </Avatar>
  );

  // If it's not the current user or dropdown is disabled, return just the avatar
  if (!isCurrentUser || !showDropdown) {
    return avatarContent;
  }

  // For current user with dropdown enabled
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`relative ${sizeClasses[size]} rounded-full p-0`}
          disabled={disabled || globalStore.loadingOverlay}
        >
          {avatarContent}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="group cursor-pointer" onClick={handleCopy}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="font-medium text-foreground">
                {userInfo?.fullName ?? userInfo?.userName ?? 'User'}
              </div>
              <div className="text-xs text-muted-foreground">{userInfo?.email}</div>
            </div>
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-muted-foreground flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>ID</span>
                    {copied ? (
                      <CheckIcon className="size-3 text-primary-foreground" />
                    ) : (
                      <CopyIcon className="size-3" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{globalStore.currentUser.uid}</p>
                </TooltipContent>
              </Tooltip>
            </>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={logout}>
          <LogOutIcon className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

UserAvatar.displayName = 'UserAvatar';
